import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN, H_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { anyReallyAny } from "@cervval.js/common/src/tools/any.tools";
import { filterNotDefined } from "@cervval.js/common/src/tools/collection.array.tools";
import { SELECTION_TYPE } from "../../components/smartTable/selectionModel/smartTableSelectionModel.types";
import { CjsSmartTable, SINGLE_COLUMN_UID, SmartTableOptions } from "../../components/smartTable/smartTable";
import { SmartTableCheckboxSelectorColumn } from "../../components/smartTable/smartTableCheckboxSelectorColumn";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

const DEFAULT_VALUE_KEY = "id";

export interface ReferenceTableEditorEditorOptions<ItemType, ValueType> extends EditorOptions {
    getItemValue: (item: ItemType) => ValueType | undefined;
    getItemByValue: (items: ItemType[], value: ValueType) => ItemType | undefined;
}

export const defaultTableEditorOptions: ReferenceTableEditorEditorOptions<anyReallyAny, anyReallyAny> = {
    ...defaultEditorOptions,
    getItemValue: item => item[DEFAULT_VALUE_KEY],
    getItemByValue: (items, value) => items.find(item => item[DEFAULT_VALUE_KEY] === value)
};

/* Group options of editor himself and table */
export type TableEditorOptions<ItemType, ValueType> = {
    options?: Partial<ReferenceTableEditorEditorOptions<ItemType, ValueType>>,
    smartTableOptions?: Partial<SmartTableOptions<ItemType>>
};

@CustomElement({
    name: "cjs-reference-table-editor",
    elementCssClasses: [FLEX_CONTAINER_COLUMN, H_100]
})
export class CjsReferenceTableEditor<ItemType, ValueType> extends CjsEditor<ValueType[]> {

    @TypedWebComponentOptions(defaultTableEditorOptions)
    public readonly options!: WebComponentOptions<ReferenceTableEditorEditorOptions<ItemType, ValueType>>;

    /** Direct access to smartTable options */
    public get smartTableOptions(): WebComponentOptions<SmartTableOptions<ItemType>> {
        return this.smartTable.options;
    }

    /** SmartTable fo tableEditor */
    public smartTable = new CjsSmartTable<ItemType>();

    /** Checkbox colum  */
    public checkBoxColumn: SmartTableCheckboxSelectorColumn<ItemType>;

    constructor(options?: Partial<TableEditorOptions<ItemType, ValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        this.options.setWithoutRefresh(options?.options);
        this.smartTableOptions.setWithoutRefresh({
            columns: [
                {
                    uid: SINGLE_COLUMN_UID
                }
            ],
            disableStrippedRows: true,
            disableHeader: true,
            border: { borderLess: true },
            parentContainerHasNoHeightConstraint: true,
            scrollBar: "standard",
            selection: {
                selectionType: SELECTION_TYPE.MULTIPLE,
                enableBackgroundDisplay: false
            },
            ...options?.smartTableOptions
        });

        this.checkBoxColumn = new SmartTableCheckboxSelectorColumn<ItemType>(this.smartTable);
        this.checkBoxColumn.width = "50px";
        this.checkBoxColumn.insertFirst();
    }

    /** Return component input */
    public getInput(): CjsSmartTable<ItemType> {
        return this.smartTable;
    }

    /** When component is initialize */
    public override init(): void {
        super.init();

        this.smartTable.selectionChangeEvent.on(() => this._onInputValueChange());

        // Add smartTable to tableEditor
        this.prepend(this.smartTable);
    }

    /**
     * Function that refresh the displayed tableEditor
     */
    public override refresh(): void {
        this.smartTable.refresh();
        super.refresh();

    }

    /**
     * @returns validationFormElement
     * Where "is-invalid" class will be added / removed
     */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().components.smartTableContainer;
    }

    /**
     * Update the input value from stored value
     */
    protected override _refreshInputValue(): void {

        const selectedItems = filterNotDefined(this._value?.map(v => this._getItemByValue(v)) ?? []);
        // Set selected value option to table
        this.smartTable.selectItems(
            selectedItems,
            (item1, item2) => {
                return this._getItemValue(item1) === this._getItemValue(item2);
            },
            {
                doNotDispatchChangeEvent: true,
                preventScroll: true
            }
        );
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): ValueType[] {
        const selectedValues: ValueType[] = [];

        // Map selected items to value
        for (const selectedItem of this.smartTable.getSelectedItems()) {
            const value = this._getItemValue(selectedItem);
            if (value != null) {
                selectedValues.push(value);
            }
        }
        return selectedValues;
    }

    /** Get value of item */
    protected _getItemValue(item: ItemType): ValueType | undefined {
        return this.options.getItemValue?.(item);
    }

    /** Get item of given value */
    protected _getItemByValue(value: ValueType): ItemType | undefined {
        return this.options.getItemByValue?.(this.smartTable.getRowsData(), value);
    }

    /** Update disabled attribute on input component */
    protected override _refreshDisabled(): void {

        for (const row of this.smartTable.tableRows) {
            const checkBox = this.checkBoxColumn.checkboxesByRow[row.uid];
            checkBox?.options.setAndRefreshIfChanged({
                disabled: this.disabled
            });
        }
    }
}